/**
 * Ad-pixel loader + event helpers.
 *
 * Loads whichever pixels are configured via /api/settings/pixels and exposes a single
 * `track(event, params)` API that fans out to Meta, TikTok, GA4, Google Ads and Snap.
 *
 * Standard events used across the funnel:
 *   PageView          — every route change (fires automatically)
 *   ViewContent       — product detail page
 *   AddToCart         — cart add
 *   InitiateCheckout  — /checkout page mount
 *   Purchase          — /order/success with payment_status=paid
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let CONFIG = null;
let CONFIG_PROMISE = null;
let USER_MATCH = {}; // Advanced Matching data (Meta auto-hashes client-side)

// Inject a <script> tag once
function addScript(id, src, opts = {}) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (src) s.src = src;
  if (opts.text) s.text = opts.text;
  document.head.appendChild(s);
}

function loadMeta(pixelId) {
  if (window.fbq) return;
  addScript("meta-pixel-base", null, {
    text: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}'${Object.keys(USER_MATCH).length ? `, ${JSON.stringify(USER_MATCH)}` : ""}); fbq('track', 'PageView');`,
  });
}

function loadTikTok(pixelId) {
  if (window.ttq) return;
  addScript("tiktok-pixel-base", null, {
    text: `!function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
ttq.load('${pixelId}'); ttq.page(); }(window, document, 'ttq');`,
  });
}

function loadGoogle(ga4Id, googleAdsId) {
  const firstId = ga4Id || googleAdsId;
  if (!firstId) return;
  addScript("gtag-src", `https://www.googletagmanager.com/gtag/js?id=${firstId}`);
  if (window.gtag) return;
  addScript("gtag-init", null, {
    text: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js', new Date());${ga4Id ? `gtag('config','${ga4Id}');` : ""}${googleAdsId ? `gtag('config','${googleAdsId}');` : ""}`,
  });
}

function loadSnap(pixelId) {
  if (window.snaptr) return;
  addScript("snap-pixel-base", null, {
    text: `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u)})(window,document,'https://sc-static.net/scevent.min.js');
snaptr('init','${pixelId}');snaptr('track','PAGE_VIEW');`,
  });
}

export async function initPixels() {
  if (CONFIG !== null) return CONFIG;
  if (CONFIG_PROMISE) return CONFIG_PROMISE;
  CONFIG_PROMISE = axios.get(`${API}/settings/pixels`).then((r) => r.data).catch(() => ({}));
  CONFIG = await CONFIG_PROMISE;
  if (CONFIG.meta_pixel_id) loadMeta(CONFIG.meta_pixel_id);
  if (CONFIG.tiktok_pixel_id) loadTikTok(CONFIG.tiktok_pixel_id);
  if (CONFIG.ga4_measurement_id || CONFIG.google_ads_id) loadGoogle(CONFIG.ga4_measurement_id, CONFIG.google_ads_id);
  if (CONFIG.snap_pixel_id) loadSnap(CONFIG.snap_pixel_id);
  addVerificationMeta("facebook-domain-verification", CONFIG.meta_domain_verification);
  addVerificationMeta("tiktok-developers-site-verification", CONFIG.tiktok_domain_verification);
  addVerificationMeta("google-site-verification", CONFIG.google_site_verification);
  addVerificationMeta("p:domain_verify", CONFIG.pinterest_site_verification);
  return CONFIG;
}

// Inject a single <meta name="..." content="..."> tag once (for ad-platform domain verification)
function addVerificationMeta(name, content) {
  if (!content || typeof document === "undefined") return;
  if (document.querySelector(`meta[name="${name}"]`)) return;
  const m = document.createElement("meta");
  m.setAttribute("name", name);
  m.setAttribute("content", content);
  document.head.appendChild(m);
}

// ---- Advanced Matching (Meta / TikTok / GA4) ----
// Improves Event Match Quality (EMQ) so Meta can attribute events to Facebook
// profiles. Meta auto-hashes these values client-side (SHA-256) before sending.
// Docs: https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
function _norm(v) { return (v == null ? "" : String(v)).trim().toLowerCase(); }

function _buildMatchData(user) {
  if (!user) return {};
  const out = {};
  const em = _norm(user.email);
  if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) out.em = em;
  const rawPh = String(user.phone || user.phone_number || "").replace(/[^\d]/g, "");
  if (rawPh) out.ph = rawPh;
  const full = _norm(user.name || user.full_name);
  if (full) {
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts[0]) out.fn = parts[0];
    if (parts.length > 1) out.ln = parts.slice(1).join(" ");
  }
  const addr = user.shipping_address || user.address || {};
  const ct = _norm(user.city || addr.city);
  if (ct) out.ct = ct.replace(/\s+/g, "");
  const st = _norm(user.state || addr.state);
  if (st) out.st = st;
  const zp = _norm(user.zip || addr.zip || addr.postal_code);
  if (zp) out.zp = zp;
  const country = _norm(user.country || addr.country);
  if (country) out.country = country.length > 2 ? country.slice(0, 2) : country;
  const eid = user.id || user._id;
  if (eid) out.external_id = String(eid);
  return out;
}

/**
 * Register hashed user data with all pixels. Improves EMQ from ~0/10 to 6-8/10.
 * Safe to call repeatedly (merges data; skips no-ops).
 */
export function setUserData(user) {
  const data = _buildMatchData(user);
  if (!Object.keys(data).length) return;
  const changed = Object.keys(data).some((k) => data[k] !== USER_MATCH[k]);
  if (!changed) return;
  USER_MATCH = { ...USER_MATCH, ...data };
  const cfg = CONFIG || {};
  try {
    if (cfg.meta_pixel_id && window.fbq) {
      // Re-init pixel with advanced matching; subsequent events inherit it.
      window.fbq("init", cfg.meta_pixel_id, USER_MATCH);
    }
    if (cfg.tiktok_pixel_id && window.ttq && window.ttq.identify) {
      window.ttq.identify({
        email: USER_MATCH.em || undefined,
        phone_number: USER_MATCH.ph ? `+${USER_MATCH.ph}` : undefined,
        external_id: USER_MATCH.external_id || undefined,
      });
    }
    if (cfg.ga4_measurement_id && window.gtag && USER_MATCH.external_id) {
      window.gtag("config", cfg.ga4_measurement_id, { user_id: USER_MATCH.external_id });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("pixel setUserData failed", e);
  }
}


// Map our internal events to each platform's native event name.
const EVENT_MAP = {
  PageView:         { meta: "PageView",         tiktok: "Browse",           ga4: "page_view",        snap: "PAGE_VIEW" },
  ViewContent:      { meta: "ViewContent",      tiktok: "ViewContent",      ga4: "view_item",        snap: "VIEW_CONTENT" },
  AddToCart:        { meta: "AddToCart",        tiktok: "AddToCart",        ga4: "add_to_cart",      snap: "ADD_CART" },
  InitiateCheckout: { meta: "InitiateCheckout", tiktok: "InitiateCheckout", ga4: "begin_checkout",   snap: "START_CHECKOUT" },
  Purchase:         { meta: "Purchase",         tiktok: "CompletePayment",  ga4: "purchase",         snap: "PURCHASE" },
};

export function track(event, params = {}) {
  const cfg = CONFIG || {};
  const names = EVENT_MAP[event]; if (!names) return;
  // Normalize params
  const value = params.value || params.price || 0;
  const currency = params.currency || "USD";
  const items = params.items || (params.product_id ? [{ id: params.product_id, quantity: params.quantity || 1, price: params.price || 0 }] : []);

  try {
    if (cfg.meta_pixel_id && window.fbq) {
      window.fbq("track", names.meta, {
        value, currency,
        content_ids: items.map((i) => i.id),
        content_type: items.length > 1 ? "product_group" : "product",
      });
    }
    if (cfg.tiktok_pixel_id && window.ttq) {
      window.ttq.track(names.tiktok, { value, currency, contents: items.map((i) => ({ content_id: i.id, quantity: i.quantity, price: i.price })) });
    }
    if ((cfg.ga4_measurement_id || cfg.google_ads_id) && window.gtag) {
      window.gtag("event", names.ga4, { value, currency, items: items.map((i) => ({ item_id: i.id, quantity: i.quantity, price: i.price })) });
      // Fire a Google Ads conversion event too, if configured
      if (event === "Purchase" && cfg.google_ads_id && cfg.google_ads_conversion_label) {
        window.gtag("event", "conversion", {
          send_to: `${cfg.google_ads_id}/${cfg.google_ads_conversion_label}`,
          value, currency,
          transaction_id: params.order_id || undefined,
        });
      }
    }
    if (cfg.snap_pixel_id && window.snaptr) {
      window.snaptr("track", names.snap, { price: value, currency });
    }
  } catch (e) {
    // Never let tracking break the app
    // eslint-disable-next-line no-console
    console.warn("pixel track failed", e);
  }
}
